import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import TrendingAssemblies from './components/TrendingAssemblies';
import PersonalizedRecommendations from './components/PersonalizedRecommendations';
import CommunityShowcase from './components/CommunityShowcase';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer'; 
import { getHomepageConfiguration } from '../../services/homepageService';
import { blogService } from '../../services/blogService';

const Homepage = () => {
  const [homepageConfig, setHomepageConfig] = useState([]);
  const [siteConfig, setSiteConfig] = useState({
    siteTitle: '3D Interactive Parts Replacements',
    siteTagline: 'Precision meets imagination. Explore our vast collection parts in 3D models and transform your creative projects with immersive visualization.'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const [config, settings] = await Promise.all([
          getHomepageConfiguration(),
          blogService.getPageSettings('site_config')
        ]);
        
        if (config) setHomepageConfig(config);
        if (settings) {
          setSiteConfig({
            siteTitle: settings.siteTitle || '3D Interactive Parts Replacements',
            siteTagline: settings.siteTagline || 'Precision meets imagination. Explore our vast collection parts in 3D models and transform your creative projects with immersive visualization.'
          });
        }
      } catch (err) {
        console.error('Error fetching homepage data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getComponentProps = (name) => {
    const config = homepageConfig.find(c => c.component_name === name);
    return config ? { 
      isVisible: config.is_visible, 
      title: config.title, 
      subtitle: config.subtitle, 
      description: config.description, 
      cta_text: config.cta_text, 
      cta_link: config.cta_link 
    } : { isVisible: true }; 
  };

  if (loading) {
    return <div>Loading homepage...</div>;
  }

  return (
    <>
      <Helmet>
        <title>OpenGadget - {siteConfig.siteTitle}</title>
        <meta 
          name="description" 
          content={siteConfig.siteTagline} 
        />
        <meta name="keywords" content="parts, parts replacement, replacments, replacement parts, 3D, 3D models, interactive visualization, 3D marketplace, architectural visualization, product design, DIY" />
        <meta property="og:title" content={`OpenGadget - ${siteConfig.siteTitle}`} />
        <meta property="og:description" content={siteConfig.siteTagline} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="/homepage" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main className="pt-16">
          {/* Hero Section */}
          {getComponentProps('hero').isVisible && ( 
            <HeroSection {...getComponentProps('hero')} />
          )}

          {getComponentProps('trendingAssemblies').isVisible && (
            <TrendingAssemblies 
              title={getComponentProps('trendingAssemblies').title} 
              subtitle={getComponentProps('trendingAssemblies').subtitle} 
              cta_text={getComponentProps('trendingAssemblies').cta_text} 
              cta_link={getComponentProps('trendingAssemblies').cta_link} 
            />
          )}

          {getComponentProps('personalizedRecommendations').isVisible && (
            <PersonalizedRecommendations 
              title={getComponentProps('personalizedRecommendations').title} 
              subtitle={getComponentProps('personalizedRecommendations').subtitle} 
            />
          )}

          {getComponentProps('communityShowcase').isVisible && (
            <CommunityShowcase 
              title={getComponentProps('communityShowcase').title} 
              subtitle={getComponentProps('communityShowcase').subtitle} 
              cta_text={getComponentProps('communityShowcase').cta_text} 
              cta_link={getComponentProps('communityShowcase').cta_link} 
            />
          )}

          {getComponentProps('callToAction').isVisible && (
            <CallToAction 
              title={getComponentProps('callToAction').title} 
              description={getComponentProps('callToAction').description} 
              cta_text={getComponentProps('callToAction').cta_text} 
              cta_link={getComponentProps('callToAction').cta_link} 
            />
          )}
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Homepage;
